import type {
  AxisContribution,
  OverallCoverage,
  OverallLevelExplanation,
} from "@shared/labs/level-assessment";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { LevelChip } from "./chips";
import { C, DM, UI } from "./ui-tokens";

/** Signed pull as a compact badge, e.g. "-0,45 nível" — the axis's exact
 *  contribution to the weighted mean (w′·(x−μ)), in level units. */
function PullBadge({ pull, tone }: { pull: number; tone: "down" | "up" }) {
  const fg = tone === "down" ? C.red : C.green;
  const bg = tone === "down" ? C.red10 : C.greenBg;
  const label = `${pull > 0 ? "+" : ""}${pull.toFixed(2).replace(".", ",")} nível`;
  return (
    <span
      style={{
        ...DM,
        fontWeight: 700,
        fontSize: 12,
        padding: "2px 8px",
        borderRadius: 999,
        background: bg,
        color: fg,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

/** Subtle chip for a competency whose read is epistemically fragile (low per-axis
 *  confidence) — an uncertainty to investigate, not a confirmed weakness. */
export function FragileChip() {
  return (
    <span
      style={{
        ...UI,
        fontWeight: 600,
        fontSize: 10.5,
        padding: "2px 8px",
        borderRadius: 999,
        background: C.ink5,
        color: C.ink50,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      baixa confiança
    </span>
  );
}

/** One axis contribution row: level chip · label · pull · interpretation. */
function ContributionRow({ c, tone }: { c: AxisContribution; tone: "down" | "up" }) {
  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
      <span style={{ flex: "none", marginTop: 1 }}>
        <LevelChip level={`L${c.level}`} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ ...DM, fontWeight: 600, fontSize: 13.5, color: C.ink }}>
            {c.axisLabel}
          </span>
          <PullBadge pull={c.pull} tone={tone} />
          {c.reliability === "fragile" && <FragileChip />}
        </div>
        {c.interpretation && (
          <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5, marginTop: 3 }}>
            {c.interpretation}
          </div>
        )}
      </div>
    </div>
  );
}

/** A pulling-down / pulling-up group with a header and its contribution rows. */
function ContributionGroup({ kind, items }: { kind: "down" | "up"; items: AxisContribution[] }) {
  const cfg =
    kind === "down"
      ? { title: "Reduzem a estimativa", color: C.red, Icon: ArrowDownRight }
      : { title: "Elevam a estimativa", color: C.green, Icon: ArrowUpRight };
  const { Icon } = cfg;
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${cfg.color}`,
        borderRadius: 14,
        padding: "18px 18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 999,
            background: kind === "down" ? C.red10 : C.greenBg,
          }}
        >
          <Icon size={15} color={cfg.color} />
        </span>
        <span style={{ ...DM, fontWeight: 700, fontSize: 14.5, color: C.ink }}>{cfg.title}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ ...UI, fontSize: 12.5, color: C.ink50, lineHeight: 1.5 }}>
          {kind === "down"
            ? "Nenhuma competência abaixo da média."
            : "Nenhuma competência acima da média."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
          {items.map((c) => (
            <ContributionRow key={c.axis} c={c} tone={kind} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Qualitative band label for the dispersion spread — shared with the lead's
 *  dispersion meter so the wording stays in sync. */
export const SPREAD_LABEL: Record<"tight" | "moderate" | "wide", string> = {
  tight: "Convergente",
  moderate: "Variação moderada",
  wide: "Perfil irregular",
};

/** Coverage notice: how much of the intended evaluation weight could not be
 *  assessed, naming the abstained competencies. Hidden at full coverage. */
function CoverageNotice({ coverage }: { coverage: OverallCoverage }) {
  if (coverage.scoredWeightMass >= 1) return null;
  const lostPct = Math.round((1 - coverage.scoredWeightMass) * 100);
  const names = coverage.abstained.map((a) => a.axisLabel || a.axis);
  return (
    <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>
      Cobertura parcial: {lostPct}% do peso da avaliação ficou sem nível
      {names.length > 0 ? (
        <>
          {" — "}
          <b style={{ color: C.ink }}>{names.join(", ")}</b> em N/D.
        </>
      ) : (
        "."
      )}
    </div>
  );
}

/**
 * The full per-competency breakdown: every scored competency grouped by whether
 * it reduces or raises the weighted-mean estimate (by signed pull), plus the
 * coverage gap. This is the detail surface — it lives behind the "Detalhamento
 * por competência" disclosure in `LevelDetail`; the lead (verdict, dispersion
 * meter, distilled drivers) carries the audience-facing read. Pure presentation
 * — numbers are Member B's deterministic attribution, prose is Member C.
 */
export function OverallExplanationPanel({ data }: { data: OverallLevelExplanation }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          alignItems: "start",
        }}
      >
        <ContributionGroup kind="down" items={data.pullingDown} />
        <ContributionGroup kind="up" items={data.pullingUp} />
      </div>
      {data.coverage && <CoverageNotice coverage={data.coverage} />}
    </div>
  );
}
